/**
 * Alta / edición administrativa de Combo — POS-COMBOS-C1A-DOMAIN-001
 * No vende Combo ni descuenta stock.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPlus, FaTrash, FaSave, FaToggleOn } from 'react-icons/fa';

import combosService from '../../services/combos.service';
import productosService from '../../services/productos.service';
import { useAuth } from '../../contexts/AuthContext';
import useEmpresaConfig from '../../hooks/useEmpresaConfig';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import { formatCurrency } from '../../utils/format';
import { resolveProductSalePrice } from '../../utils/listasPreciosLabels';
import {
  calcularEconomiaLocal,
  escenariosRecomendador
} from '../../utils/comboEconomics';

const ComboForm = () => {
  const { id } = useParams();
  const esEdicion = Boolean(id);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { empresaConfig } = useEmpresaConfig();
  const listaKey = empresaConfig?.lista_precio_punto_venta_default || 'mayorista';

  const puedeCrear = hasPermission('combos', 'crear');
  const puedeEditar = hasPermission('combos', 'editar');
  const puedeGuardar = esEdicion ? puedeEditar : puedeCrear;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productos, setProductos] = useState([]);
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [precioVenta, setPrecioVenta] = useState('');
  const [componentes, setComponentes] = useState([]);
  const [estadoActual, setEstadoActual] = useState('draft');
  const [serverErrors, setServerErrors] = useState([]);

  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        const prods = await productosService.obtenerActivos().catch(() => productosService.obtenerTodos());
        setProductos(Array.isArray(prods) ? prods : []);

        if (esEdicion) {
          const combo = await combosService.obtenerPorId(id);
          setNombre(combo.nombre || '');
          setDescripcion(combo.descripcion || '');
          setPrecioVenta(combo.precioVenta != null ? String(combo.precioVenta) : '');
          setComponentes(
            Array.isArray(combo.componentes)
              ? combo.componentes.map((c) => ({
                  productId: c.productId,
                  cantidad: c.cantidad,
                  nombre_snapshot: c.nombre_snapshot,
                  codigo_snapshot: c.codigo_snapshot
                }))
              : []
          );
          setEstadoActual(combo.estado || 'draft');
          if (Array.isArray(combo.activationErrors)) {
            setServerErrors(combo.activationErrors);
          }
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || 'No se pudo cargar el Combo');
        navigate('/combos');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [esEdicion, id, navigate]);

  const productosById = useMemo(() => {
    const map = {};
    productos.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [productos]);

  const economia = useMemo(
    () =>
      calcularEconomiaLocal({
        componentes,
        precioVenta,
        productosById,
        listaKey
      }),
    [componentes, precioVenta, productosById, listaKey]
  );

  const escenarios = useMemo(
    () =>
      escenariosRecomendador({
        sumaPreciosIndividuales: economia.sumaPreciosIndividuales,
        costoTotal: economia.costoTotal
      }),
    [economia.sumaPreciosIndividuales, economia.costoTotal]
  );

  const productosFiltrados = useMemo(() => {
    const term = busquedaProducto.trim().toLowerCase();
    const selected = new Set(componentes.map((c) => c.productId));
    return productos
      .filter((p) => !selected.has(p.id))
      .filter((p) => {
        if (!term) return true;
        const hay = `${p.nombre || ''} ${p.codigo || ''} ${p.codigo_barras || ''}`.toLowerCase();
        return hay.includes(term);
      })
      .slice(0, 12);
  }, [productos, componentes, busquedaProducto]);

  const agregarProducto = (producto) => {
    setComponentes((prev) => [
      ...prev,
      {
        productId: producto.id,
        cantidad: 1,
        nombre_snapshot: producto.nombre || null,
        codigo_snapshot: producto.codigo || null
      }
    ]);
    setBusquedaProducto('');
  };

  const actualizarCantidad = (productId, cantidad) => {
    setComponentes((prev) =>
      prev.map((c) => (c.productId === productId ? { ...c, cantidad } : c))
    );
  };

  const quitarComponente = (productId) => {
    setComponentes((prev) => prev.filter((c) => c.productId !== productId));
  };

  const payloadBase = () => ({
    nombre: nombre.trim(),
    descripcion: descripcion.trim() || null,
    precioVenta: Number(String(precioVenta).replace(',', '.')),
    componentes: componentes.map((c) => ({
      productId: c.productId,
      cantidad: Number(c.cantidad),
      nombre_snapshot: c.nombre_snapshot || null,
      codigo_snapshot: c.codigo_snapshot || null
    }))
  });

  const guardar = async ({ activar = false } = {}) => {
    if (!puedeGuardar) {
      toast.error('Sin permiso para guardar Combos');
      return;
    }
    if (!nombre.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      setSaving(true);
      setServerErrors([]);
      const body = payloadBase();
      if (activar) body.estado = 'active';

      let saved;
      if (esEdicion) {
        saved = await combosService.actualizar(id, body);
      } else {
        saved = await combosService.crear({ ...body, estado: activar ? 'active' : 'draft' });
      }

      if (activar && esEdicion && saved?.estado !== 'active') {
        await combosService.activar(id);
      }

      toast.success(activar ? 'Combo guardado y activado' : 'Combo guardado');
      navigate('/combos');
    } catch (error) {
      const data = error?.response?.data;
      const errors = data?.errors || [];
      setServerErrors(errors);
      toast.error(data?.message || 'No se pudo guardar el Combo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">
            {esEdicion ? 'Editar Combo' : 'Nuevo Combo'}
          </h1>
          <p className="text-e0e3-muted mt-1">
            Estado actual: <span className="text-e0e3-foreground">{estadoActual}</span>
            {' · '}también conocido como Pack / Kit / Selección
          </p>
        </div>
        <Button color="secondary" icon={<FaArrowLeft />} onClick={() => navigate('/combos')}>
          Volver
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 space-y-4">
          <div>
            <label className="block text-sm text-e0e3-muted mb-1">Nombre</label>
            <input
              className="input w-full"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej. Combo desayuno"
            />
          </div>
          <div>
            <label className="block text-sm text-e0e3-muted mb-1">Descripción (opcional)</label>
            <textarea
              className="input w-full min-h-[80px]"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-e0e3-muted mb-1">Precio de venta del Combo</label>
            <input
              className="input w-full"
              type="number"
              min="0"
              step="0.01"
              value={precioVenta}
              onChange={(e) => setPrecioVenta(e.target.value)}
            />
          </div>

          <div className="border-t border-white/10 pt-4 space-y-3">
            <h2 className="text-lg font-semibold text-e0e3-foreground">Componentes</h2>
            <input
              className="input w-full"
              value={busquedaProducto}
              onChange={(e) => setBusquedaProducto(e.target.value)}
              placeholder="Buscar producto por nombre o código…"
            />
            {busquedaProducto.trim() && (
              <div className="rounded border border-white/10 divide-y divide-white/5 max-h-56 overflow-y-auto">
                {productosFiltrados.length === 0 ? (
                  <p className="p-3 text-sm text-e0e3-muted">Sin resultados</p>
                ) : (
                  productosFiltrados.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-white/[0.04] flex justify-between gap-3"
                      onClick={() => agregarProducto(p)}
                    >
                      <span>
                        <span className="text-e0e3-foreground">{p.nombre}</span>
                        <span className="block text-xs text-e0e3-muted">{p.codigo}</span>
                      </span>
                      <span className="text-sm text-e0e3-muted">
                        {formatCurrency(resolveProductSalePrice(p, listaKey))}
                        <FaPlus className="inline ml-2 text-e0e3-ok" />
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}

            {componentes.length === 0 ? (
              <p className="text-sm text-e0e3-muted">Agregá productos al Combo.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="nexo-table w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left">Producto</th>
                      <th className="text-right">Cant.</th>
                      <th className="text-right">P. unit.</th>
                      <th className="text-right">Costo</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {economia.detalle.map((row) => (
                      <tr key={row.productId}>
                        <td>
                          <div className="font-medium text-e0e3-foreground">{row.nombre}</div>
                          <div className="text-xs text-e0e3-muted">{row.codigo}</div>
                        </td>
                        <td className="text-right">
                          <input
                            className="input w-20 text-right ml-auto"
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={row.cantidad}
                            onChange={(e) => actualizarCantidad(row.productId, e.target.value)}
                          />
                        </td>
                        <td className="text-right">{formatCurrency(row.precioUnitario || 0)}</td>
                        <td className="text-right">
                          {row.costoUnitario != null ? formatCurrency(row.costoUnitario) : (
                            <span className="text-e0e3-danger">Sin costo</span>
                          )}
                        </td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="text-e0e3-danger"
                            onClick={() => quitarComponente(row.productId)}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        <div className="space-y-4">
          <Card className="space-y-2">
            <h2 className="text-lg font-semibold text-e0e3-foreground">Economía</h2>
            <div className="flex justify-between text-sm">
              <span className="text-e0e3-muted">Suma precios individuales</span>
              <span>{formatCurrency(economia.sumaPreciosIndividuales || 0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-e0e3-muted">Precio Combo</span>
              <span>{economia.precioVenta != null ? formatCurrency(economia.precioVenta) : '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-e0e3-muted">Descuento</span>
              <span>
                {economia.descuentoAbsoluto != null
                  ? `${formatCurrency(economia.descuentoAbsoluto)} (${economia.descuentoPct ?? 0}%)`
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-e0e3-muted">Costo total</span>
              <span>{economia.costoTotal != null ? formatCurrency(economia.costoTotal) : '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-e0e3-muted">Ganancia</span>
              <span>{economia.ganancia != null ? formatCurrency(economia.ganancia) : '—'}</span>
            </div>
            <div className="flex justify-between text-sm font-medium">
              <span className="text-e0e3-muted">Margen s/ venta</span>
              <span>{economia.margenPct != null ? `${economia.margenPct}%` : '—'}</span>
            </div>
          </Card>

          <Card className="space-y-2">
            <h2 className="text-lg font-semibold text-e0e3-foreground">Recomendador L1</h2>
            <p className="text-xs text-e0e3-muted">Escenarios determinísticos de descuento (sin IA).</p>
            {escenarios.map((esc) => (
              <button
                key={esc.descuentoPct}
                type="button"
                className={`w-full text-left rounded border px-3 py-2 text-sm ${
                  esc.viable
                    ? 'border-e0e3-ok/40 hover:bg-white/[0.03]'
                    : 'border-white/10 opacity-60'
                }`}
                onClick={() => setPrecioVenta(String(esc.precioPropuesto))}
              >
                <div className="flex justify-between">
                  <span>Dto {esc.descuentoPct}%</span>
                  <span>{formatCurrency(esc.precioPropuesto)}</span>
                </div>
                <div className="text-xs text-e0e3-muted">
                  Margen {esc.margenPct != null ? `${esc.margenPct}%` : '—'}
                  {esc.viable ? '' : ' · no viable'}
                </div>
              </button>
            ))}
          </Card>

          {(economia.activationErrors.length > 0 || serverErrors.length > 0) && (
            <Card className="border border-e0e3-danger/40 space-y-1">
              <h3 className="font-medium text-e0e3-danger">Bloqueos de activación</h3>
              {[...economia.activationErrors, ...serverErrors].map((err, idx) => (
                <p key={`${err.code}-${idx}`} className="text-sm text-e0e3-muted">
                  {err.message || err.code}
                </p>
              ))}
              <p className="text-xs text-e0e3-muted pt-1">
                Podés guardar como borrador y completar después.
              </p>
            </Card>
          )}

          <div className="flex flex-col gap-2">
            <Button
              color="primary"
              icon={<FaSave />}
              disabled={saving || !puedeGuardar}
              onClick={() => guardar({ activar: false })}
            >
              Guardar borrador
            </Button>
            <Button
              color="secondary"
              icon={<FaToggleOn />}
              disabled={saving || !puedeGuardar || !economia.canActivate}
              onClick={() => guardar({ activar: true })}
            >
              Guardar y activar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComboForm;
